import { createReducer, on } from '@ngrx/store';
import { markCached } from '@ps/utils';

import { INITIAL_NOTIFICATION_STATE } from './notification.constants';
import { NotificationState } from './notification.model';
import {
  NotificationActions,
  NotificationApiActions,
} from './notification.actions';
import {
  withKnownActionStates,
  withResult,
} from './notification-action-state.util';

function withoutApplyingId(
  applyingNotificationIds: string[],
  notificationId: string,
): string[] {
  return applyingNotificationIds.filter((id) => id !== notificationId);
}

export const notificationReducer = createReducer(
  INITIAL_NOTIFICATION_STATE,

  on(
    NotificationApiActions.loadNotificationsStarted,
    (state): NotificationState => ({
      notifications: state.notifications,
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: true,
      error: null,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),

  on(
    NotificationApiActions.loadNotificationsSuccess,
    (state, { notifications, cachedAt }): NotificationState => ({
      notifications: withKnownActionStates(notifications, state.notifications),
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: false,
      error: null,
      notificationsCachedAt: cachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),

  on(
    NotificationApiActions.loadNotificationsFailure,
    (state, { error }): NotificationState => ({
      notifications: [],
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: false,
      error,
      notificationsCachedAt: null,
      unseenCountCachedAt: state.unseenCountCachedAt,
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),

  on(
    NotificationApiActions.loadUnseenCountSuccess,
    (
      state,
      { unseenCount, communicationCount, needsActionCount, cachedAt },
    ): NotificationState => ({
      notifications: state.notifications,
      unseenCount,
      communicationCount,
      needsActionCount,
      loading: state.loading,
      error: null,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: cachedAt,
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),

  on(
    NotificationApiActions.loadUnseenCountFailure,
    (state, { error }): NotificationState => ({
      notifications: state.notifications,
      unseenCount: 0,
      communicationCount: null,
      needsActionCount: null,
      loading: state.loading,
      error,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: null,
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),

  on(
    NotificationActions.invalidateCache,
    (state): NotificationState => ({
      notifications: state.notifications,
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: state.loading,
      error: state.error,
      notificationsCachedAt: null,
      unseenCountCachedAt: null,
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),

  on(
    NotificationActions.applyAction,
    NotificationActions.undoAction,
    (state, { notificationId }): NotificationState => ({
      notifications: state.notifications,
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: state.loading,
      error: null,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
      applyingNotificationIds: state.applyingNotificationIds.includes(
        notificationId,
      )
        ? state.applyingNotificationIds
        : [...state.applyingNotificationIds, notificationId],
    }),
  ),

  on(
    NotificationApiActions.applyActionSuccess,
    NotificationApiActions.undoActionSuccess,
    (state, { notificationId, actionId, result }): NotificationState => ({
      notifications: state.notifications.map((notification) =>
        notification.notificationId === notificationId
          ? {
              ...notification,
              actions: notification.actions.map((action) =>
                action.actionId === actionId
                  ? withResult(action, result)
                  : action,
              ),
            }
          : notification,
      ),
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: state.loading,
      error: null,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
      applyingNotificationIds: withoutApplyingId(
        state.applyingNotificationIds,
        notificationId,
      ),
    }),
  ),

  on(
    NotificationApiActions.applyActionFailure,
    NotificationApiActions.undoActionFailure,
    (state, { notificationId, error }): NotificationState => ({
      notifications: state.notifications,
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: state.loading,
      error,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
      applyingNotificationIds: withoutApplyingId(
        state.applyingNotificationIds,
        notificationId,
      ),
    }),
  ),

  on(
    NotificationApiActions.markAllViewedSuccess,
    (state): NotificationState => ({
      notifications: state.notifications.map((notification) => ({
        ...notification,
        viewed: true,
      })),
      unseenCount: 0,
      communicationCount: state.communicationCount === null ? null : 0,
      needsActionCount: state.needsActionCount === null ? null : 0,
      loading: state.loading,
      error: null,
      notificationsCachedAt: markCached(),
      unseenCountCachedAt: markCached(),
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),

  on(
    NotificationApiActions.markViewedSuccess,
    (state, { notification }): NotificationState => {
      const previous = state.notifications.find(
        (existingNotification) =>
          existingNotification.notificationId === notification.notificationId,
      );
      // Computed locally rather than left for the follow-up refetch: that
      // refetch is now behind a 60s cache gate (see notification.effects.ts)
      // and can be silently skipped, leaving the badge stale.
      const unseenDelta = !previous
        ? 0
        : Number(previous.viewed) - Number(notification.viewed);

      return {
        notifications: state.notifications.map((existingNotification) =>
          existingNotification.notificationId === notification.notificationId
            ? notification
            : existingNotification,
        ),
        unseenCount: Math.max(0, state.unseenCount + unseenDelta),
        // Left untouched here — refreshAfterViewedMutation$ re-fetches the
        // authoritative counts right after this, since which tab's count
        // moves depends on that one notification's action state, not
        // something this handler has enough information to derive.
        communicationCount: state.communicationCount,
        needsActionCount: state.needsActionCount,
        loading: state.loading,
        error: null,
        notificationsCachedAt: markCached(),
        unseenCountCachedAt: markCached(),
        applyingNotificationIds: state.applyingNotificationIds,
      };
    },
  ),

  on(
    NotificationApiActions.markAllViewedFailure,
    NotificationApiActions.markViewedFailure,
    (state, { error }): NotificationState => ({
      notifications: state.notifications,
      unseenCount: state.unseenCount,
      communicationCount: state.communicationCount,
      needsActionCount: state.needsActionCount,
      loading: state.loading,
      error,
      notificationsCachedAt: state.notificationsCachedAt,
      unseenCountCachedAt: state.unseenCountCachedAt,
      applyingNotificationIds: state.applyingNotificationIds,
    }),
  ),
);
